import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/core/utils';
import { PricingTier, BillingCycle, TierAccent } from './types';

interface PricingCardProps {
  tier: PricingTier;
  billingCycle: BillingCycle;
  index: number;
  onSelect?: (tierId: string) => void;
  isSelected?: boolean;
  isLoading?: boolean;
}

// Styling configurations per tier accent
const tierStyles: Record<TierAccent, {
  border: string;
  glow: string;
  accent: string;
  headerBg: string;
  badge: string;
  button: string;
  icon: string;
  gradient: string;
  checkColor: string;
}> = {
  grow: {
    border: 'border-border',
    glow: '',
    accent: 'bg-grow',
    headerBg: 'bg-grow text-white',
    badge: 'bg-grow/10 text-grow border-grow/20',
    button: 'bg-grow hover:bg-grow/90 text-white',
    icon: 'text-grow',
    gradient: '',
    checkColor: 'text-grow',
  },
  primary: {
    border: 'border-border',
    glow: '',
    accent: 'bg-primary',
    headerBg: 'bg-primary text-white',
    badge: 'bg-primary/10 text-primary border-primary/20',
    button: 'bg-primary hover:bg-primary/90 text-white',
    icon: 'text-primary',
    gradient: '',
    checkColor: 'text-primary',
  },
  secondary: {
    border: 'border-border',
    glow: '',
    accent: 'bg-secondary',
    headerBg: 'bg-secondary text-white',
    badge: 'bg-secondary/10 text-secondary border-secondary/20',
    button: 'bg-secondary hover:bg-secondary/90 text-white',
    icon: 'text-secondary',
    gradient: '',
    checkColor: 'text-secondary',
  },
  gradient: {
    border: 'border-border',
    glow: '',
    accent: 'bg-gradient-to-r from-grow to-primary',
    headerBg: 'bg-gradient-to-r from-primary via-grow to-primary text-white',
    badge: 'bg-gradient-to-r from-grow/10 to-primary/10 text-foreground border-grow/20',
    button: 'bg-gradient-to-r from-primary via-grow to-primary bg-[length:200%_100%] hover:bg-right text-white transition-all duration-500',
    icon: 'text-grow',
    gradient: '',
    checkColor: 'text-grow',
  },
};

export const PricingCard: React.FC<PricingCardProps> = ({
  tier,
  billingCycle,
  index,
  onSelect,
  isSelected,
  isLoading,
}) => {
  const styles = tierStyles[tier.accent];
  const isPopular = tier.isPopular;

  const displayPrice = tier.monthlyPrice !== null
    ? billingCycle === 'yearly'
      ? Math.round(tier.yearlyPrice! / 12)
      : tier.monthlyPrice
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.21, 0.47, 0.32, 0.98],
      }}
      className={cn(
        'relative group h-full',
        isPopular && 'z-10'
      )}
    >
      {/* Popular badge */}
      {isPopular && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="absolute -top-4 left-1/2 -translate-x-1/2 z-20"
        >
          <Badge className="bg-secondary text-white px-4 py-1.5 shadow-lg border-0 font-medium">
            <Star className="w-3.5 h-3.5 mr-1.5" fill="currentColor" />
            Most Popular
          </Badge>
        </motion.div>
      )}

      <div
        className={cn(
          'relative h-full rounded-2xl border-2 transition-all duration-500 overflow-hidden',
          'flex flex-col',
          isSelected
            ? 'border-primary ring-2 ring-primary ring-offset-2'
            : styles.border,
          styles.glow,
          onSelect && 'cursor-pointer hover:border-primary/50'
        )}
        onClick={onSelect && !isLoading ? () => onSelect(tier.id) : undefined}
      >
        {/* Header section with colored background */}
        <div className={cn('p-6 lg:p-8 text-center', styles.headerBg)}>
          {/* Name */}
          <div className="mb-4">
            <h3 className="text-xl font-bold">{tier.name}</h3>
          </div>

          {/* Price */}
          <div>
            {displayPrice !== null ? (
              <motion.div
                key={`${tier.id}-${billingCycle}`}
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-baseline gap-1 justify-center">
                  <span className="text-4xl lg:text-5xl font-bold tracking-tight">
                    €{displayPrice}
                  </span>
                  <span className="text-white/70 text-lg">/mo</span>
                </div>
                {billingCycle === 'yearly' && (
                  <p className="text-sm text-white/70 mt-1">
                    €{tier.yearlyPrice} billed annually
                  </p>
                )}
              </motion.div>
            ) : (
              <div>
                <div className="text-4xl lg:text-5xl font-bold tracking-tight">
                  Custom
                </div>
                <p className="text-sm text-white/70 mt-1">
                  Tailored to your needs
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Content section with card background */}
        <div className="relative p-6 lg:p-8 flex flex-col flex-grow bg-card">
          {/* Features List */}
          <ul className="space-y-3 mb-8 flex-grow">
            {tier.features.map((feature, idx) => (
              <motion.li
                key={feature}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + idx * 0.05, duration: 0.3 }}
                className="flex items-start gap-3"
              >
                <Check className={cn('w-5 h-5 mt-0.5 flex-shrink-0', styles.checkColor)} />
                <span className="text-sm text-foreground">{feature}</span>
              </motion.li>
            ))}
          </ul>

          {/* CTA Button */}
          {onSelect ? (
            <Button
              variant="custom"
              size="lg"
              className={cn(
                'w-full py-6 text-base font-semibold group/btn transition-all duration-300',
                styles.button
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (!isLoading) onSelect(tier.id);
              }}
              disabled={isLoading}
            >
              {isLoading && isSelected ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {tier.ctaText}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="custom"
              size="lg"
              className={cn(
                'w-full py-6 text-base font-semibold group/btn transition-all duration-300',
                styles.button
              )}
              asChild
            >
              <Link to={tier.ctaLink}>
                {tier.ctaText}
                <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
            </Button>
          )}

          {/* Trial note */}
          {displayPrice !== null && (
            <p className="text-xs text-center text-muted-foreground mt-4">
              14-day free trial included
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PricingCard;
